import { useEffect, useRef, type Ref } from "react";
import { markdownToHtml } from "@heylife/core/markdown";
import { htmlToMarkdown } from "./legalHtml";
import { useT } from "./i18n";

export function readLegalRichMarkdown(html: string): string {
  return htmlToMarkdown(html);
}

/** WYSIWYG editor for legal copy; persisted as markdown. */
export function LegalRichEditor({
  value,
  onChange,
  editorRef,
}: {
  value: string;
  onChange: (markdown: string) => void;
  editorRef?: Ref<HTMLDivElement | null>;
}) {
  const { t } = useT();
  const ref = useRef<HTMLDivElement>(null);
  const lastMarkdown = useRef(value);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (value === lastMarkdown.current && el.childNodes.length > 0) return;
    el.innerHTML = markdownToHtml(value) || "<p><br></p>";
    lastMarkdown.current = value;
  }, [value]);

  const sync = () => {
    const el = ref.current;
    if (!el) return;
    const md = htmlToMarkdown(el.innerHTML);
    lastMarkdown.current = md;
    onChange(md);
  };

  const run = (command: string, arg?: string) => {
    ref.current?.focus();
    document.execCommand(command, false, arg);
    sync();
  };

  return (
    <div className="legal-rich">
      <div className="legal-rich-toolbar" role="toolbar" aria-label={t("admin.settings.legalToolbar")}>
        <button type="button" className="btn small ghost" onMouseDown={(e) => e.preventDefault()} onClick={() => run("bold")}>
          <strong>B</strong>
        </button>
        <button type="button" className="btn small ghost" onMouseDown={(e) => e.preventDefault()} onClick={() => run("italic")}>
          <em>I</em>
        </button>
        <button type="button" className="btn small ghost" onMouseDown={(e) => e.preventDefault()} onClick={() => run("formatBlock", "h2")}>
          H1
        </button>
        <button type="button" className="btn small ghost" onMouseDown={(e) => e.preventDefault()} onClick={() => run("formatBlock", "h3")}>
          H2
        </button>
        <button type="button" className="btn small ghost" onMouseDown={(e) => e.preventDefault()} onClick={() => run("insertUnorderedList")}>
          •
        </button>
        <button type="button" className="btn small ghost legal-align-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => run("justifyLeft")} title={t("admin.settings.legalAlignLeft")} aria-label={t("admin.settings.legalAlignLeft")}>
          <span className="legal-align-glyph legal-align-glyph-left" aria-hidden>
            <span /><span /><span />
          </span>
        </button>
        <button type="button" className="btn small ghost legal-align-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => run("justifyCenter")} title={t("admin.settings.legalAlignCenter")} aria-label={t("admin.settings.legalAlignCenter")}>
          <span className="legal-align-glyph legal-align-glyph-center" aria-hidden>
            <span /><span /><span />
          </span>
        </button>
        <button type="button" className="btn small ghost legal-align-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => run("justifyRight")} title={t("admin.settings.legalAlignRight")} aria-label={t("admin.settings.legalAlignRight")}>
          <span className="legal-align-glyph legal-align-glyph-right" aria-hidden>
            <span /><span /><span />
          </span>
        </button>
      </div>
      <div
        ref={(node) => {
          ref.current = node;
          if (typeof editorRef === "function") editorRef(node);
          else if (editorRef) editorRef.current = node;
        }}
        className="legal-rich-editor"
        contentEditable
        role="textbox"
        aria-multiline
        suppressContentEditableWarning
        onInput={sync}
        data-placeholder={t("admin.settings.legalPlaceholder")}
      />
    </div>
  );
}
